import { useEffect, useState } from 'react';
import { FindLineItemVariables, IFindAllLineItemHeaders, IFindLineItems, ILineItemHeader } from '../types';
import { QUERY_FIND_ALL_LINE_ITEMS_HEADERS, QUERY_FIND_LINE_ITEMS } from 'graphql/queries/lineItems';
import { useLazyQuery } from '@apollo/client';

export const useLineItemsData = (recordTypeId: number, headers?: ILineItemHeader) => {
    const [lineItemHeaders, setLineItemHeaders] = useState<ILineItemHeader | undefined>(headers);
    const [currentRecordTypeId, setCurrentRecordTypeId] = useState<number | undefined>(recordTypeId);

    const [getAllHeaders, { loading: loadingLineItems }] = useLazyQuery<IFindAllLineItemHeaders>(QUERY_FIND_ALL_LINE_ITEMS_HEADERS, {
        onCompleted(data) {
            const foundHeaders = data.findAllLineItemsByType.find((itemData) => {
                const recordTypesArr = itemData.recordType.map((el) => Number(el.id));
                return recordTypesArr.includes(recordTypeId as number);
            });
            setLineItemHeaders(foundHeaders || undefined);
        }
    });

    const [getLineItems, { data: lineItemsData, loading: loadingList }] = useLazyQuery<IFindLineItems, FindLineItemVariables>(
        QUERY_FIND_LINE_ITEMS
    );

    const loading = loadingLineItems || loadingList;

    useEffect(() => {
        // If recordTypeId changed, clear current headers to force reload
        if (currentRecordTypeId !== recordTypeId) {
            setLineItemHeaders(undefined);
            setCurrentRecordTypeId(recordTypeId);
        }

        // If headers are provided and match the current recordTypeId, use them
        if (headers) {
            // Check if the provided headers match the current recordTypeId
            const headersMatchRecordType = headers.recordType?.some((rt) => Number(rt.id) === recordTypeId);
            if (headersMatchRecordType) {
                setLineItemHeaders(headers);
                return;
            }
        }
        // Fetch headers if not provided or if they don't match the current recordTypeId
        // Always fetch when recordTypeId changes to ensure we have the correct headers
        if (recordTypeId && recordTypeId > 0) {
            getAllHeaders();
        } else {
            // Clear headers if recordTypeId is invalid
            setLineItemHeaders(undefined);
        }
    }, [getAllHeaders, headers, recordTypeId, currentRecordTypeId]);

    return { lineItemHeaders, getLineItems, lineItemsData, loading };
};

export default useLineItemsData;
